/**
 * The desk API client.
 *
 * Every call is same-origin /api/*. The Pages Function in functions/api/
 * forwards it to the desk with the caller's Cloudflare Access assertion; see
 * that file for why it is not a cross-origin fetch.
 *
 * There is no API key here and there never will be. Identity is the Access
 * session; a dashboard that carried a credential of its own would be a second
 * way into the desk, which is the shape of the v1 incident this whole rebuild
 * is answering.
 */
import type { TrackSpec } from './track';
import type { Progress } from './approving';
export type { Progress };

export class DeskError extends Error {
  status: number;
  next: string | null;
  /** The refusal's whole body, for a route that says more than one thing (a template save's `problems`). */
  body: unknown;
  constructor(message: string, status: number, next: string | null = null, body: unknown = null) {
    super(message);
    this.status = status;
    this.next = next;
    this.body = body;
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try {
    r = await fetch(`/api${path}`, {
      ...init,
      // The Access cookie is same-origin, but say so explicitly: the day this
      // is ever served from somewhere else, the omission would be silent.
      credentials: 'include',
      headers: { accept: 'application/json', ...(init?.body ? { 'content-type': 'application/json' } : {}) },
    });
  } catch (e) {
    throw new DeskError(`the desk could not be reached: ${(e as Error).message}`, 0);
  }

  const text = await r.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* not JSON; handled below */ }

  if (!r.ok) {
    // The desk says why in one of two shapes. A route that refuses the request
    // answers `{error}`; an executor that refused to act answers
    // `{ok:false, reason, next}` — which is the more important of the two,
    // because it is the one carrying "outbound_halt is on" or "they have paid
    // since this card was raised". Reading only `error` showed the operator a
    // bare "409" where the desk had written a sentence explaining itself.
    const b = body as { error?: string; reason?: string; next?: string } | null;
    throw new DeskError(
      b?.error ?? b?.reason ?? `the desk refused this (${r.status})`,
      r.status,
      b?.next ?? null,
      body,
    );
  }
  return body as T;
}

const get = <T>(path: string) => call<T>(path);
const post = <T>(path: string, body: unknown = {}) =>
  call<T>(path, { method: 'POST', body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Shapes. These mirror workers/lib/desk/api.js; the route snapshot test in the
// PSA repo is what stops them drifting apart.
// ---------------------------------------------------------------------------

export type Bucket = 'needs_you' | 'veto' | 'waiting' | 'done' | 'done_today' | 'failed' | 'quiet' | 'all';

export interface EvidenceLine { fact: string; observed: string | null }

export interface AttachmentRead {
  id: number;
  filename: string;
  kind: 'image' | 'pdf';
  label: string;
  pages: number | null;
  saw: string | null;
}

/** A question the drafter could not answer, what it searched, and the fact it needs; then your answer (fix-40). */
export interface UnansweredQuestion {
  question: string;
  searched: string[];
  needed: string | null;
  answer?: string;
  answered_by?: string;
  answered_at?: string;
  article_id?: number;
  /** partly-1 (PSA #312): the 0-based place in `questions` of the question it is part of; null or absent: none. */
  of?: number | null;
}

/** An attachment the drafter read, served by the desk from R2 (kept 30 days). */
export const attachmentUrl = (id: number) => `/api/attachments/${id}`;

export interface CardPayload {
  /** fix-67: a sales lead's one allowed promise, while it is pending. */
  callback?: string | null;
  /** fix-81: a card raised from a call to our sales line — the number, when, a topic and one line; never the call. */
  source?: 'call';
  phone?: string | null;
  called_at?: string;
  topic?: string;
  summary?: string;
  to?: string;
  subject?: string;
  body?: string;
  template_id?: number;
  thread_key?: string;
  vars?: Record<string, string>;
  changes?: { field: string; before: string | null; after: string | null }[];
  steps?: string[];
  refinements?: { instruction: string; at: string }[];
  /** fix-23: sentences the drafter reasoned to rather than read, with what it reasoned from. Always held for you. */
  inferred?: { sentence: string; from: string[] }[];
  /** fix-40: the questions the draft leaves out for you to answer; never a promise to the customer. */
  unanswered_questions?: UnansweredQuestion[];
  /** answer-1: every question they asked, in their words, and how the reply answers it. */
  questions?: { question: string; how: QuestionHow | null; marked?: QuestionHow | null }[];
  /** fix-27: the images and PDFs the drafter was shown, and what it said it saw in each. */
  attachments_read?: AttachmentRead[];
  /** fix-75: approved, and waiting for the portal; the drain tries it every ten minutes until `until` (UTC). */
  portal_wait?: PortalWait;
  [k: string]: unknown;
}

/** What an approval answers (fix-79): `running` while the run continues on the desk, else its result. */
export interface Started { ok: boolean; running?: boolean; summary?: string; reason?: string; next?: string; card: Card }

export interface PortalWait { since: string; until: string; tries: number; last_try: string; reason: string }

export interface Card {
  id: number;
  type: string;
  class: string;
  status: string;
  account_no: string | null;
  email_id: number | null;
  title: string;
  summary: string | null;
  confidence: number | null;
  needs_human: number;
  veto_deadline: string | null;
  /** When a pending veto card will really send: its deadline, or 08:00 if that falls in quiet hours. */
  sends_at?: string;
  quiet_hours?: boolean;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
  executed_at: string | null;
  error_text: string | null;
  customer_name: string | null;
  customer_email: string | null;
  evidence: EvidenceLine[];
  payload: CardPayload;
  /** fix-67: on a sales_lead, the newest reply drafted to its emails. */
  lead_draft?: { id: number; status: string; body: string | null } | null;
  /** fix-73: what the card screen shows beside the card (GET /cards/:id and the editor's writes). */
  workspace?: Workspace;
  /** escalated-1 (ADR-0361): on a reply draft, passed to our voice engineers — the one sentence the reply may then carry. */
  escalation?: { by: string; at: string; sentence: string } | null;
}

export interface PreflightCheck {
  key: string;
  label: string;
  state: 'pass' | 'refuse' | 'note';
  detail: string;
  at: string | null;
}

export interface Preflight {
  ok: boolean;
  signal: string | null;
  reason: string | null;
  next: string | null;
  /**
   * Would approving this EVER work? True when the refusal is final — the
   * customer paid, called or wrote in, or the rules would not choose this
   * account today. The desk expires such a card on approval rather than failing
   * it, so it never reaches Retry. Optional because a desk older than soak-3
   * does not send it, and absent must read as "not known to be final".
   */
  permanent?: boolean;
  checks: PreflightCheck[];
  card_created_at: string;
  consequential: boolean;
}

export interface Stats {
  cards: Record<Bucket, number>;
  outbound_halt: boolean;
  model_spend_today_usd: number;
  veto_next_deadline: string | null;
}

export interface WorkerHealth {
  worker: string;
  /** `late` is a run that SUCCEEDED and is older than its worker's tolerance —
   *  a cron that has simply stopped firing, which used to read 'ok' for ever. */
  state: 'ok' | 'late' | 'failed' | 'running' | 'stalled' | 'never';
  max_age_minutes?: number | null;
  last_started_at: string | null;
  last_finished_at: string | null;
  ok: boolean | null;
  minutes_ago: number | null;
}

export interface Health {
  environment: string | null;
  deploy_sha: string | null;
  you: { email: string; role: string } | null;
  bindings: Record<string, boolean>;
  workers: WorkerHealth[];
}

export interface StoredEmail {
  id: number;
  direction: string;
  from_addr: string | null;
  to_addr: string | null;
  subject: string | null;
  received_at: string | null;
  body_text: string | null;
  thread_key: string | null;
  thread_count: number;
  /** fix-73: the thread's other messages, oldest first. */
  thread?: { id: number; direction: string; received_at: string | null; opening: string }[];
}

export interface LifelineRow {
  account_no: string;
  name: string | null;
  status: string | null;
  started_at: string | null;
  day: number | null;
  called: number;
  first_call_at: string | null;
  calls_total: number;
  extended_until: string | null;
  /** The first email we started (isOutreach), never a reply or a service notice. */
  reached_out_at: string | null;
  /** The offer email: the template the closure row waits on. */
  extension_offered_at: string | null;
  /** trial_extended: the grant, not the offer. */
  extension_granted_at: string | null;
  closure_sent_at: string | null;
  /** Unpaid, open, and in its first lifeline days, a running extension or an open offer: only these get a next step. */
  in_trial: boolean;
  paid: number;
  /** Suspended, Cancelled or Closed; IOU (exp) is open. */
  closed: number;
  /** From `payments` (fix-331): how many, their total inc GST (0 when none), and the first and second dates. */
  payments_total: number;
  paid_total: number;
  converted_at: string | null;
  second_paid_at: string | null;
  stuck: boolean;
  outcome: 'first_payment' | 'second_payment' | 'paid_then_left' | 'closed_unpaid' | 'lapsed' | 'stuck' | 'using it' | 'new';
  /** The lifecycle step the selector would raise next if nothing changes, on the moved clock. */
  next_due: { template_id: number; title: string; closes: boolean; in_days: number; due_at: string } | null;
  next_move: string;
  next_move_card_id: number | null;
}

export interface Lifeline {
  /** Every sign-up in the period, newest first (fix-331, ADR-0356). */
  rows: LifelineRow[];
  period_days: number;
  /** The periods the desk offers (`settings.lifeline_periods`). */
  periods: number[];
  sign_ups: number;
  /** Sign-ups with any payment, and with a second one. */
  converted: number;
  second_payment: number;
  /** converted ÷ sign-ups; null with no sign-ups. */
  conversion_rate: number | null;
  /** Every payment the period's sign-ups made, inc GST. */
  revenue: number;
  in_trial: number;
  closed: number;
  stuck: number;
  /** Trials whose next step inside the window is the closure card. */
  closures_due: number;
  window_days: number;
  track: Omit<TrackSpec, 'window_days'>;
}

/**
 * One policy class on the Classes screen. Plain 30-day counts — `sent` is
 * what went out, whoever decided it — and the mode, which a person changes
 * here and nothing promotes on its own (Phase 8.4).
 */
export interface ClassRow {
  class: string;
  label: string;
  detail: string;
  mode: 'approve' | 'veto' | 'hold' | 'auto';
  veto_hours: number | null;
  stop_limit_30d: number;
  /** consequential and fraud_sweep: decided by policy, not changed here. */
  fixed: boolean;
  mode_changed_at: string | null;
  mode_changed_by: string | null;
  sent: number;
  stopped: number;
  dismissed: number;
  rejected: number;
  /** Signups the checks rejected and a person activated with a reason, last 30 days (fix-15). */
  overridden?: number;
}

export type SettableMode = 'approve' | 'veto' | 'hold';

export interface SpendDay { day: string; by_model: Record<string, number>; total: number }

export interface Outcome {
  label: string;
  outcome: string;
  count: number;
  tone: 'plain' | 'good' | 'bad';
}

/**
 * One holdout experiment's standing.
 *
 * `verdict` is a sentence, not a number, until `conclusive` — a difference
 * computed on twenty accounts would mean nothing and would be quoted.
 */
export interface ExperimentResult {
  experiment_id: string;
  question: string;
  treatment: { n: number; paid: number; called: number };
  holdout: { n: number; paid: number; called: number };
  total: number;
  conclusive: boolean;
  /** Running: compareArms' sentence. Stopped: the result recorded when it was stopped (fix-87, #233), never judged. */
  verdict: string;
  status: 'running' | 'stopped';
  started_at: string;
  stopped_at: string | null;
}

/** One word-diff run: '=' kept, '-' removed, '+' added (workers/lib/word-diff.js). */
export type DiffOp = ['=' | '-' | '+', string];

/** fix-73: mirrors workers/lib/desk/workspace.js workspaceOf(). */
export interface Workspace {
  versions?: { v: number; kind: string; at: string }[];
  ai_draft?: string | null;
  share_changed?: number;
  state: { status: string | null; last_paid_at: string | null; balance: number | null; balance_age: string | null;
    lines: string } | null;
  drafter_read?: { pages: string[]; links: string[]; live: string[]; inferred: number; inferred_from: string[] };
  trial_history?: TrialHistory | null;
}

/** fix-83: mirrors workers/lib/desk/trial-history.js — on every card for an account that has never paid. */
export interface TrialHistory {
  signed_up_at: string | null;
  lines: { at: string; day: number | null; kind: string; text: string; card_id: number | null }[];
  now: { card_id: number; title: string | null; day: number | null; facts: string };
}

/** fix-73: the rules line for the text in the editor (POST /cards/:id/check). */
export interface TextCheck {
  ok: boolean;
  refusal: string | null;
  would_fail: string[];
  flags: string[];
  share_changed: number;
  diff: DiffOp[];
}

/** fix-73: the Monday card of the week's edits, by kind (workers/lib/knowledge/edits.js). */
export interface EditGroup {
  kind: 'shortened' | 'promise' | 'fact' | 'tone' | 'opening';
  label: string;
  count: number;
  pairs: { card_id: number; feedback_id: number; before: string; after: string }[];
  rule_line: string;
}

export interface Classes {
  classes: ClassRow[];
  spend: { days: SpendDay[]; total_usd: number; by_model: { model: string; usd: number }[] };
  outcomes: Outcome[];
  outcomes_n: number;
  /** Sends already made and not yet old enough to judge. */
  outcomes_waiting: number;
  /** False below N = 100. There is no rate at all below it, not a hedged one. */
  outcomes_rates_unlocked: boolean;
  outcomes_note: string;
  outcomes_source: string;
  experiments: ExperimentResult[];
  halt: { on: boolean; last_changed_at: string | null; last_changed_by: string | null };
  /** fix-62 (#193): reply drafts since settings.drafter_measure_from; per_20 is Refines + Rejects, never answers. */
  drafter: DrafterMeasure;
}

export interface MeasureCounts {
  /** Set instead of the numbers when the setting is missing or nonsense. */
  error?: string;
  from: string; drafts: number; refines: number; rejects: number; answers: number; per_20: number | null;
  /** fix-73: the average share of the AI's first draft changed before sending, 0–100, over `sent` drafts. */
  sent?: number; share_changed?: number | null;
  /** answer-1: questions per 20 drafts, over the `drafts` that carry the question list. */
  questions?: { drafts: number } & Record<QuestionHow, number | null>;
}
/** partly-1: `partly` is set by the desk, never the model — a question with a gap in unanswered_questions. */
export type QuestionHow = 'knowledge' | 'inference' | 'partly' | 'operator';
export const HOW_LABEL: Record<QuestionHow, string> = { knowledge: 'from knowledge', inference: 'by inference',
  partly: 'partly left to you', operator: 'left to you' };
/** Since the model's change (drafter_measure_from), and — fix-57 — since the Drafter instructions were last saved. */
export interface DrafterMeasure extends MeasureCounts {
  instructions?: MeasureCounts;
  last_20?: { sent: number; share_changed: number | null };
}

/**
 * One invariant, as the Health screen shows it.
 *
 * `incident` is the thing that actually happened — the reason this check
 * exists. It is shown on screen next to the check, including when the check is
 * green, because these are worthless the moment nobody remembers what they are
 * watching for.
 */
export interface Invariant {
  id: string;
  label: string;
  why: string;
  incident: string;
  /**
   * Three colours: green `clear`; red `failing` or `broken` ("could not run"
   * is never clear); grey `acknowledged` — known, owned, with a reason and a
   * date, and not failing until that date passes or a new row appears.
   */
  state: 'clear' | 'failing' | 'broken' | 'acknowledged';
  count: number;
  sample: string[];
  error: string | null;
  acknowledged: { until: string; reason: string; by: string; at: string } | null;
  /** Why an acknowledged check is red again. */
  realert: { reason: 'lapsed' | 'new_rows' | 'broken'; until?: string; rows?: string[] } | null;
  /** When it entered its current state; null when no run recorded it. */
  since: string | null;
  /**
   * Rows the check counted and left out on purpose — "1 protected, not chased"
   * (#196, fix-60): grey beside the check in any state, never red.
   */
  aside?: { count: number; rows: string[]; label: string } | null;
}

export interface WorkerPing {
  ok: boolean;
  state: 'ok' | 'degraded' | 'unreachable' | 'not_bound';
  status?: number;
  deploy_sha?: string | null;
  environment?: string | null;
  missing_bindings?: string[];
  detail?: string;
}

export interface JobAge {
  state: 'ok' | 'late' | 'failed' | 'running' | 'stalled' | 'never';
  ok: boolean;
  age_minutes?: number;
  max_age_minutes?: number;
  detail?: string;
}

export interface Invariants {
  ran_at: string | null;
  ok: boolean | null;
  signature: string;
  checks: Invariant[];
  failing: number;
  acknowledged: number;
  max_ack_days: number;
  pings: Record<string, WorkerPing>;
  jobs: Record<string, JobAge>;
  posting_note: string;
}

export interface SentYesterday {
  day: string;
  total: number;
  by_class: { class: string; template_id: number | null; count: number }[];
  note: string;
}

// ---------------------------------------------------------------------------


// ---- Finance (F2). Mirrors workers/lib/desk/finance.js and lib/finance/monthly.js in the PSA repo. ----

/** One Hero billing month (the New Zealand calendar month), as the ledger rebuilds it nightly. */
export interface FinanceMonth {
  month: string;
  cash_in_inc: number | null; cash_in_ex: null;
  charges_recurring_ex: number | null; charges_recurring_inc: number | null;
  charges_usage_ex: number | null; charges_usage_inc: number | null;
  charges_one_off_ex: number | null; charges_one_off_inc: number | null;
  credits_other_ex: number | null; credits_other_inc: number | null;
  trial_credit_ex: number | null; trial_credit_inc: number | null; trial_credits: number | null;
  charges_uncollectable_ex: number | null; charges_uncollectable_inc: number | null;
  charges_trial_credit_ex: number | null; charges_trial_credit_inc: number | null;
  prepaid_liability_inc: number | null; debt_exposure_inc: number | null; debt_by_arrears_json: string | null;
  paying_active: number | null; in_trial: number | null; converted: number | null; cancelled: number | null; net: number | null;
  wholesale_currency: string | null; wholesale_nzd_inc: number | null; wholesale_nzd_ex_portal: number | null;
  wholesale_invoice_nzd: number | null; wholesale_invoice_date: string | null;
  wholesale_cost_basis: 'inc' | 'ex' | null;
  fx_rate: number | null; fx_source: 'fx_rates' | 'operator default' | 'none' | null; fx_effective_date: string | null;
  wholesale_aud: number | null; gross_margin_aud: number | null; arpu_aud_ex: number | null;
  cash_in_source: 'stripe' | 'portal' | null; cash_in_portal_inc: number | null;
  payment_fees_inc: number | null; payment_fees_ex: number | null;
  as_of: string; deploy_sha: string | null; source_notes: string;
}

export interface VerdictMetric { key: string; name: string; value: number | null; average: number | null; months: number; compare: 'above' | 'below' | 'level' | 'not comparable' }
export interface Verdict {
  month: string;
  verdict: 'moving forward' | 'holding' | 'moving backward' | 'in progress' | 'no data';
  sentence: string;
  concern: { key: string; text: string } | null;
  metrics: VerdictMetric[];
}

export interface FinanceMonthly {
  month: string;
  cost_basis: 'inc' | 'ex';
  rows: FinanceMonth[];
  verdict: Verdict | null;
  queries: Record<string, string>;
}

export interface Rate { value: number | null; source: 'fx_rates' | 'operator default' | 'none' | null; effective_date: string | null }

export interface ItemRow {
  key: string; label: string;
  charged_ex: number; charged_inc: number;
  hero_nzd_ex: number; hero_nzd_inc: number; hero_lines: number | null;
  hero_aud_ex: number | null; hero_aud_inc: number | null; margin_aud: number | null;
}
/** A wholesale_finding card (Finance F3), as the finance routes list it. */
export interface Finding {
  id: number; status: string; title: string; summary?: string | null; kind: string; period: string; account_no: string | null;
  amount_nzd: number | null; amount_aud: number | null; previous_card_id: number | null;
  created_at: string; decided_at: string | null; decided_by: string | null; evidence?: EvidenceLine[];
}
export interface FinanceFindings {
  period: string; from: string;
  paid_for_nothing: { month: { nzd: number; aud: number | null; findings: number }; trailing_6: { nzd: number; aud: number | null; findings: number }; kinds: string[] };
  by_kind: { kind: string; findings: Finding[] }[];
}
export interface AccountMonth {
  month: string; account_no: string; name: string | null; status: string | null; class: string;
  charged_ex: number; charged_json: string; hero_nzd_inc: number; hero_json: string; chargeable_s: number;
  calling_nzd_inc: number; hero_aud: number | null; fees_ex: number; margin_aud: number | null; fx_rate: number | null;
  uncollectable_ex: number | null; uncollectable_inc: number | null;
  trial_credit_ex: number | null; trial_credit_inc: number | null;
}
/** GET /api/finance/d1 (PSA #290, FIN-52): D1 rows this database's scheduled runs read, and the period's projected bill. */
export interface FinanceD1 {
  period: { start: string; end: string; counted_from: string | null };
  days: { day: string; rows_read: number; runs: number }[];
  today: number; period_rows: number; projected_rows: number | null; projected_usd: number | null;
  day_limit: number; included_rows: number; usd_per_billion: number; counts: string;
  queries: Record<string, string>;
}
export interface FinanceAccounts { period: string; accounts: AccountMonth[]; queries: Record<string, string> }
export interface ByItem {
  period: string; bill_date: string | null; rate: Rate; cost_basis: 'inc' | 'ex';
  items: ItemRow[];
  total: { charged_ex: number; charged_inc: number; hero_nzd_ex: number; hero_nzd_inc: number; hero_aud_ex: number | null; hero_aud_inc: number | null; margin_aud: number | null };
  beneath: { one_off_ex: number | null; credits_other_ex: number | null; trial_credit_ex: number | null; trial_credits: number | null };
  cancelled: { label: string; charged_ex: number; charged_inc: number; margin_aud: number | null };
  trial_credit: { label: string; charged_ex: number; charged_inc: number; margin_aud: number | null };
  collected: { charged_ex: number; charged_inc: number; margin_aud: number | null };
  month_totals: { charges_applied_ex: number | null; wholesale_aud: number | null; gross_margin_aud: number | null };
  audit: Finding[];
  queries: Record<string, string>;
}

export interface FinanceCustomers {
  period: string;
  movement: { start: number | null; converted: number | null; cancelled: number | null; end: number | null; other: number | null };
  top: { account_no: string; name: string | null; charges_ex: number; in_arrears: number; share: number | null }[];
  top_share: number | null;
  trials: {
    from: string; to: string; signups: number; converted: number; conversion: number | null;
    trial_credit_ex: number; trial_credit_consumed_ex: number; trial_credits: number; hero_cost_unpaid_aud: number; hero_cost_unrated_nzd: number;
    hero_cost_note: string; cost_per_conversion_aud: number | null; arpu_aud_ex: number | null;
  };
  queries: Record<string, string>;
}

export interface StatementLine { kind: 'invoice' | 'payment'; posted_at: string; amount_nzd: number; reference: string; currency: string; nzd: number; period_total_nzd?: number | null; matches?: boolean }
export interface FxRateRow { id: number; effective_date: string; aud_spent: number; nzd_received: number; rate: number; source: string; entered_by: string; entered_at: string }
export interface FinanceStatement {
  period: string;
  invoices: StatementLine[];
  payments: StatementLine[];
  fx_rates: FxRateRow[];
  fx_default: number | null;
  rate: Rate | null;
  basis_note: string;
  queries: Record<string, string>;
}

// ---- Knowledge (fix-22). Mirrors workers/lib/desk/knowledge.js in the PSA repo. ----
export type Audience = 'internal' | 'public';
export interface KnowledgeRow {
  id: number; slug: string; title: string; source: string; status: string; audience: Audience; weight: number;
  url: string | null; supersedes_id: number | null; superseded_by: number | null; updated_at: string | null;
  length: number; editable: boolean; always_on: boolean;
}
export interface KnowledgeArticle extends Omit<KnowledgeRow, 'superseded_by' | 'length'> {
  body_md: string; superseded_by: { id: number; title: string } | null;
}
export interface KnowledgeVersion {
  id: number; body_md: string | null; snapshot_json: string | null; changed_by: string | null; reason: string | null;
  created_at: string;
}
export interface KnowledgeFields {
  title: string; body_md: string; audience: Audience; weight: number; supersedes_id: number | null; reason: string;
}

// ---- The drafter (fix-57). Mirrors workers/lib/desk/drafter.js in the PSA repo. ----
/** One part of the system prompt: `instructions` and `article` are edited as articles here; `code` is fixed. */
export interface PromptSegment {
  kind: 'code' | 'instructions' | 'article'; label: string; text: string; slug?: string; article_id?: number | null;
}
export interface BodyRule { rule: string; action: 'refused' | 'flagged'; pinned: string | null }
export interface ModelPrice { in: number; out: number; read?: number }
export interface DrafterModel {
  draft: string | null; triage: string | null; measure_from: string | null;
  choices: (ModelPrice & { id: string })[];
  price?: ModelPrice | null; per_draft?: number | null; drafts_counted?: number;
  cache?: { warm: boolean; last_call_at?: string; model?: string; cache_read?: number; cache_write?: number };
}
export interface Drafter {
  segments: PromptSegment[]; prompt_chars: number;
  tools: { name: string; description: string; input_schema: unknown }[];
  body_rules: BodyRule[]; model: DrafterModel;
}

// ---- Templates (spec 014). Mirrors workers/lib/desk/templates.js in the PSA repo: read by anyone (Stage 1), the
// wording saved by the operator (Stage 2). ----
export type TemplateState = 'on' | 'off' | 'none';
export type TemplateDecides = 'hold' | 'veto' | 'approve' | 'auto';
/** A rule the template's text breaks (lib/template-check.js), with its words; a save's rules are saveProblems'. */
export interface TemplateProblem { rule: string; words: string | null; says: string }

// notices-1 (spec 012 BCST-26 to BCST-34): the Notices screen. The desk owns the audiences, the checks and the preview.
export type NoticeAudience = 'in_service' | 'paying' | 'trials' | 'arrears' | 'named';
export interface NoticeCount { label: string; accounts: number; addresses: number }
/** Named accounts (BCST-37 to BCST-41): the distinct numbers pasted, and each not resolved with why. */
export interface NamedCount extends NoticeCount {
  pasted: string[]; duplicates: number; unknown: string[]; no_address: string[];
  out_of_service: { account_no: string; status: string }[];
}
export interface NoticeDraft { subject: string; body: string; audience: NoticeAudience; accounts?: string }
export interface NoticeCheck {
  problems: TemplateProblem[];
  flags: TemplateProblem[];
  audiences: Record<Exclude<NoticeAudience, 'named'>, NoticeCount> & { named: NamedCount };
  preview: { account_no: string; name: string | null; subject: string; text: string; unfilled: string[] } | null;
  taken: { template_id: number; card_id: number | null } | null;
}
export interface TemplateRow {
  id: number; subject: string | null; body: string; family: string | null; state: TemplateState; class: string | null;
  decides: TemplateDecides | null; veto_hours: number | null; problems: TemplateProblem[];
  last_sent: string | null; sent_30d: number; sent_all: number;
}
/** The lifecycle row, raw; the screen says it in words (lib/templates.ts). */
export interface LifecycleRow {
  template_id: number; family: string; day_from: number | null; day_to: number | null;
  requires_template_sent: number | null; requires_days_after: number | null; class: string; consequential: number;
  suspends_on_send: number; enabled: number; resend_days: number | null; contact_window_days: number | null;
  follow_up_days: number | null; [k: string]: unknown;
}
export interface TemplatePreview { case: string | null; subject: string; text: string; unfilled: string[] }
export interface TemplateDetail extends TemplateRow {
  row: LifecycleRow | null; cases: Record<string, Record<string, string>>; placeholders: string[];
  filled_from: { source: 'last_sent' | 'typed' | 'sample'; account_no: string | null; sent_at?: string };
  previews: TemplatePreview[]; counts_note: string;
  /** Stage 2: whether the operator may save it, and why not (no rule sends it; a broadcast unfinished). */
  editable: boolean; locked: string | null;
  /** The sentences a save keeps word for word, and whether it carries the hardship footer. */
  pinned: { where: 'subject' | 'body'; sentence: string; row: number | null }[]; footer: boolean;
  allowed_placeholders: string[];
  /** Every earlier text, newest first; version_id is the newest, the base a save names. */
  versions: TemplateVersion[]; version_id: number | null;
}
export interface TemplateVersion {
  id: number; subject: string | null; body: string; cases: Record<string, Record<string, string>> | null;
  changed_by: string; reason: string; created_at: string;
}
export interface TemplateEdit {
  subject: string; body: string; cases?: Record<string, Record<string, string>>;
}

export const api = {
  stats: () => get<Stats>('/stats'),
  health: () => get<Health>('/health'),
  cards: (bucket: Bucket, order: 'oldest' | 'newest' = 'oldest') =>
    get<{ cards: Card[] }>(`/cards?bucket=${bucket}&order=${order}`),
  card: (id: number) => get<{ card: Card }>(`/cards/${id}`),
  preflight: (id: number) => get<Preflight>(`/cards/${id}/preflight`),
  email: (id: number) => get<{ email: StoredEmail }>(`/emails/${id}`),
  lifeline: (days?: number) => get<Lifeline>(`/lifeline${days ? `?days=${days}` : ''}`),
  classes: () => get<Classes>('/classes'),
  invariants: () => get<Invariants>('/invariants'),
  sentYesterday: () => get<SentYesterday>('/sent-yesterday'),
  finance: {
    monthly: (period?: string) => get<FinanceMonthly>(`/finance/monthly${period ? `?period=${period}` : ''}`),
    byItem: (period: string) => get<ByItem>(`/finance/by-item?period=${period}`),
    customers: (period: string) => get<FinanceCustomers>(`/finance/customers?period=${period}`),
    statement: (period: string) => get<FinanceStatement>(`/finance/statement?period=${period}`),
    accounts: (period: string) => get<FinanceAccounts>(`/finance/accounts?period=${period}`),
    findings: (period: string) => get<FinanceFindings>(`/finance/findings?period=${period}`),
    d1: () => get<FinanceD1>('/finance/d1'),
    addFxRate: (body: { effective_date: string; aud_spent: number; nzd_received: number; source?: string }) =>
      post<{ ok: boolean; id: number; rate: number }>('/finance/fx-rates', body),
    /** Poll Hero and Stripe and rebuild now, as the 03:05 run does (operator; desk -> ledger's FinanceRefresh). */
    refresh: () => post<{ ok: boolean; periods_polled: number; months: number; observed_at: string }>('/finance/wholesale/refresh'),
  },

  // fix-79: `running` (HTTP 202) — claimed and approved; the run continues on the desk and /progress follows it.
  approve: (id: number, body?: { body?: string; operator_override_reason?: string; send_unanswered?: boolean }) =>
    post<Started>(`/cards/${id}/approve`, body ?? {}),
  progress: (id: number) => get<Progress>(`/cards/${id}/progress`),
  reject: (id: number, reason: string) => post<{ ok: boolean; card: Card }>(`/cards/${id}/reject`, { reason }),
  // fix-67: a sales lead was called; the outcome is recorded as an events note.
  called: (id: number, outcome: string) =>
    post<Started>(`/cards/${id}/called`, { outcome }),
  // A signup its checks rejected (fix-15): confirm it (stays Pending, blacklisted), or activate with a reason.
  confirmRejection: (id: number) =>
    post<Started>(`/cards/${id}/confirm-rejection`, {}),
  overrideSignup: (id: number, reason: string) =>
    post<Started>(`/cards/${id}/override`, { reason }),
  // The card was RIGHT and events overtook it. Not reject: a rejection is the
  // drafter's training signal and is counted against the class, and neither is
  // true of a card that has simply been superseded.
  dismiss: (id: number, reason: string) => post<{ ok: boolean; card: Card }>(`/cards/${id}/dismiss`, { reason }),
  edit: (id: number, body: { body: string; subject?: string; reason?: string }) =>
    post<{ ok: boolean; card: Card }>(`/cards/${id}/edit`, body),
  // fix-73: the rules line, as he types; reads only.
  check: (id: number, body: string) => post<TextCheck>(`/cards/${id}/check`, { body }),
  answers: (id: number, body: { answers: { i: number; fact: string }[] } | { saved: { i: number; article_id: number } }) =>
    post<{ ok: boolean; note?: string; card: Card }>(`/cards/${id}/answers`, body),
  refine: (id: number, instruction: string) =>
    post<{ ok: boolean; reason?: string; note?: string; card: Card }>(`/cards/${id}/refine`, { instruction }),
  note: (id: number, text: string) => post<{ ok: boolean }>(`/cards/${id}/note`, { text }),
  stop: (id: number, reason: string) => post<{ ok: boolean; card: Card }>(`/cards/${id}/stop`, { reason }),
  retry: (id: number) => post<{ ok: boolean; card: Card }>(`/cards/${id}/retry`, {}),
  closeDoneByHand: (id: number, note: string) => post<{ ok: boolean; card: Card }>(`/cards/${id}/close`, { note }),
  escalate: (id: number, undo = false) => post<{ ok: boolean; card: Card }>(`/cards/${id}/escalate`, { undo }),
  // #217: a card waiting for the portal, run now (operator). 202 = still waiting, with why.
  tryNow: (id: number) =>
    post<Started & { waiting?: boolean }>(`/cards/${id}/try-now`, {}),
  // assign-1: an unmatched sender is matched to an account by hand; the exact address is remembered.
  customer: (accountNo: string) =>
    get<{ customer: { account_no: string; name: string | null; status: string | null; email: string | null } }>(`/customers/${encodeURIComponent(accountNo)}`),
  assign: (id: number, accountNo: string) =>
    post<{ ok: boolean; address: string; attached: number[]; card: Card }>(`/cards/${id}/assign`, { account_no: accountNo }),
  acknowledge: (id: string, days: number, reason?: string) =>
    post<{ ok: boolean }>(`/invariants/${id}/acknowledge`, { days, reason }),
  halt: (on: boolean, reason?: string) => post<{ ok: boolean; outbound_halt: boolean }>('/halt', { on, reason }),
  knowledge: {
    list: () => get<{ articles: KnowledgeRow[] }>('/knowledge'),
    get: (id: number) => get<{ article: KnowledgeArticle; versions: KnowledgeVersion[] }>(`/knowledge/${id}`),
    create: (f: KnowledgeFields) => post<{ ok: boolean; article: { id: number } }>('/knowledge', f),
    save: (id: number, f: KnowledgeFields) => post<{ ok: boolean }>(`/knowledge/${id}`, f),
    retire: (id: number, reason: string) => post<{ ok: boolean }>(`/knowledge/${id}/retire`, { reason }),
  },
  templates: {
    list: () => get<{ templates: TemplateRow[]; counts_note: string }>('/templates'),
    get: (id: number, account?: string) =>
      get<TemplateDetail>(`/templates/${id}${account ? `?account=${encodeURIComponent(account)}` : ''}`),
    // Stage 2 (operator): the live rules line and preview, which writes nothing; and the save, which a 400 refuses
    // with `problems` (on DeskError.body) and a 409 when it was saved since the editor opened.
    check: (id: number, edit: TemplateEdit, account?: string) =>
      post<{ problems: TemplateProblem[]; previews: TemplatePreview[] }>(
        `/templates/${id}/check${account ? `?account=${encodeURIComponent(account)}` : ''}`, edit),
    save: (id: number, edit: TemplateEdit & { reason: string; base_version: number | null; restored_from?: number | null }) =>
      post<{ ok: boolean; version_id: number; changed: string[] }>(`/templates/${id}`, edit),
  },
  notices: {
    audiences: () => get<{ audiences: Record<NoticeAudience, NoticeCount> }>('/notices'),
    check: (d: NoticeDraft) => post<NoticeCheck>('/notices/check', d),
    // A 400 refuses with `problems` (on DeskError.body); a 409 names the notice raised with this subject today.
    raise: (d: NoticeDraft) => post<{ ok: boolean; template_id: number; card_id: number; recipients: number }>('/notices', d),
  },
  drafter: {
    get: () => get<Drafter>('/drafter'),
    setModel: (model: string) =>
      post<{ ok: boolean; changed: boolean; before?: string; model: string }>('/drafter/model', { model, confirm: true }),
  },
  setMode: (cls: string, mode: SettableMode, veto_hours: number | null, reason?: string) =>
    post<{ ok: boolean; changed: boolean; cleared: number }>(`/classes/${cls}/mode`, { mode, veto_hours, reason }),
};

/**
 * The address to send someone to. Both hostnames below work; this is the one
 * an operator should have bookmarked.
 */
export const CANONICAL_HOST = 'desk-app-staging.click2call.com.au';

/**
 * Hostnames that have Cloudflare Access in front of them.
 *
 * A Pages project answers on its pages.dev hostname as well as on any custom
 * domain — the same shape as the *.workers.dev address that made v1's approve
 * route callable without authentication. Both of these are destinations on the
 * one Access application, so both can sign an operator in.
 *
 * The PER-DEPLOYMENT preview hostnames (`<hash>.click2call-dashboard-v4.
 * pages.dev`) are deliberately NOT here: each deploy mints a new one and they
 * are not covered until a wildcard destination is added. They serve the
 * console, cannot sign anyone in, and every API call through them is refused.
 *
 * This list is a signpost, never a control. It decides which sentence an
 * operator reads after a 401; what may actually be done is decided by the desk
 * verifying a JWT, which no browser list can affect.
 */
const SIGN_IN_HOSTS = new Set([
  CANONICAL_HOST,
  'click2call-dashboard-v4.pages.dev',
  // Local development, where there is no Access and nowhere else to send
  // anyone.
  'localhost',
  '127.0.0.1',
]);

/**
 * Is this failure "your session lapsed", or "you are at an address that has no
 * sign-in in front of it"? They look identical to the app — both are a 401
 * from a desk that saw no assertion — and the useful advice is opposite: one
 * is fixed by reloading and the other never is.
 */
export function signInAdvice(hostname: string): { message: string; url: string } | null {
  if (SIGN_IN_HOSTS.has(hostname)) return null;
  return {
    message: `This address has no sign-in in front of it, so it cannot reach the desk however `
           + `many times you reload. The console lives at ${CANONICAL_HOST}.`,
    url: `https://${CANONICAL_HOST}`,
  };
}
